import { AppError } from '../utils/appErrors';
import { getAuthTransportExpectation, reportSessionFailure } from './sessionSignals';

type FetchImplementation = (
  input: RequestInfo | URL,
  init?: RequestInit,
) => Promise<Response>;

const EXPLICIT_PUBLIC_API_PREFIXES = [
  '/api/public/',
  '/api/service-tickets/public/',
  '/api/signature-documents/public/',
  '/api/payments/public/',
  '/api/labor-calendar/public/',
  '/api/venue-precontract-intakes/public/',
  '/api/post-event/public/',
] as const;

const EXPLICIT_PUBLIC_API_PATHS = new Set([
  '/api/metrics/track',
  '/api/auth/validate-invitation',
  '/api/auth/dev-session',
]);

function requestUrl(input: RequestInfo | URL): URL | null {
  try {
    if (typeof input === 'string') return new URL(input, window.location.href);
    if (input instanceof URL) return input;
    return new URL(input.url, window.location.href);
  } catch {
    return null;
  }
}

function requestHeaders(input: RequestInfo | URL, init?: RequestInit): Headers {
  const request = typeof Request !== 'undefined' && input instanceof Request ? input : null;
  const headers = new Headers(request?.headers);
  new Headers(init?.headers).forEach((value, key) => headers.set(key, value));
  return headers;
}

function isProtectedAppEndpoint(url: URL): boolean {
  const path = url.pathname;
  if (!path.startsWith('/api/') && !path.startsWith('/ai/')) return false;
  if (EXPLICIT_PUBLIC_API_PATHS.has(path)) return false;
  return !EXPLICIT_PUBLIC_API_PREFIXES.some((prefix) => path.startsWith(prefix));
}

/**
 * Last-line protection for legacy raw fetch calls. Internal `/api/*` and
 * `/ai/*` requests on staff routes must carry the coordinator's exact current
 * token; otherwise they fail before reaching the network.
 */
export function createProtectedAppFetch(
  fetchImplementation: FetchImplementation,
): FetchImplementation {
  return async (input, init) => {
    const url = requestUrl(input);
    const expectation = getAuthTransportExpectation();
    const protectedEndpoint = Boolean(url && isProtectedAppEndpoint(url));
    if (protectedEndpoint && expectation.mode !== 'public') {
      const authorization = requestHeaders(input, init).get('authorization') ?? '';
      const bearerToken = authorization.match(/^Bearer\s+(.+)$/i)?.[1]?.trim() ?? '';
      if (
        expectation.mode === 'blocked'
        || !expectation.accessToken
        || bearerToken !== expectation.accessToken
      ) {
        reportSessionFailure({ reason: 'missing_bearer_token', source: 'api' });
        throw new AppError(
          'session_expired',
          'Your session expired. Sign in again to continue.',
          { context: 'protected_fetch_guard' },
        );
      }
    }
    const response = await fetchImplementation(input, init);
    if (protectedEndpoint && expectation.mode === 'session') {
      if (response.status === 401) {
        reportSessionFailure({
          reason: 'protected_request_unauthorized',
          source: 'api',
          status: 401,
        });
      } else if (response.status === 403) {
        reportSessionFailure({
          reason: 'protected_request_forbidden',
          source: 'api',
          status: 403,
        });
      }
    }
    return response;
  };
}

const INSTALL_MARKER = '__itaProtectedFetchGuardInstalled';

export function installProtectedAppFetchGuard(): void {
  const target = globalThis as typeof globalThis & Record<string, unknown>;
  if (target[INSTALL_MARKER] === true || typeof globalThis.fetch !== 'function') return;
  const originalFetch = globalThis.fetch.bind(globalThis);
  globalThis.fetch = createProtectedAppFetch(originalFetch) as typeof globalThis.fetch;
  target[INSTALL_MARKER] = true;
}
